import React from "react";
import { theme, Tooltip, Modal, Select,Button } from 'antd';
import {
  PlusCircleFilled,
  ClearOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  CloseCircleFilled,
  CheckCircleFilled,
  RedoOutlined,
  FilePdfOutlined
} from '@ant-design/icons';
import ColorButton from "../../common/ColorButton";
import TSTable from "../../common/TSTable";
import AddStockChemicalModal from "../../components/chemical/AddStockChemicalModal";
import { URL_CHEMICAL } from "../../constants/urls";
import { currencyFormat, toFixed, toHumanDate } from "../../frameworks/Util";
import ErrorMessages from "../../common/ErrorMessages";
import { POST } from "../../frameworks/HttpClient";
import { PRINT_COLOR } from "../../constants/color";
import { PATH } from "../../routes/CustomRoutes";

export default function PureChemicalStockList(props) {
  const [ errorMessages, setErrorMessages ] = React.useState(false);
  const [ stockTarget, setStockTarget] = React.useState(null);
  const [ filterClose, setFilterClose] = React.useState(false);
  const [ tableKey, setTableKey] = React.useState(0);
  const tableRef = React.useRef(null);
  const {
    token: { colorSuccess },
  } = theme.useToken();

  const { token : { colorError, colorPrimary, colorWarning }} = theme.useToken();

  const columns = [
    {
      title: 'Action',
      dataIndex: '',
      width : 150,
      key: 'action',
      render: (_, original) => {
        return (
          <div 
            style={{textAlign:'center'}}
            onClick={(e) => {
              e.stopPropagation()
            }}
          >
            {!original.is_closed && <Tooltip triger='hover' title="ล้างสต๊อก">
              <ColorButton 
                type='primary'
                override={colorPrimary}
                icon={<ClearOutlined />}
                style={{ margin : 5}}
                onClick={()=> confirm(original.lot_no, original.id)} 
              />
            </Tooltip>}
            {!original.is_closed && <Tooltip triger='hover' title="แก้ไข">
              <ColorButton 
                type='primary'
                override={colorWarning}
                icon={<EditOutlined />}
                style={{ margin : 5}}
                onClick={()=> setStockTarget(original)} 
              />
            </Tooltip>}
            {original.is_closed && <Tooltip triger='hover' title="ย้อนกลับ">
              <ColorButton 
                type='primary'
                override={PRINT_COLOR}
                icon={<RedoOutlined />}
                style={{ margin : 5}}
                onClick={()=>  confirmRevert(original.lot_no, original.id)} 
              />
            </Tooltip>}
          </div>
      )}
    },
    {
      title: 'วันที่นำเข้า',
      dataIndex: 'input_date',
      key: 'input_date',
      render: (value) => toHumanDate(value)
    },
    {
      title: 'ชื่อเคมีภัณฑ์',
      dataIndex: 'chemical_name',
      key: 'chemical_name',
      searcher: true,
    },
    {
      title: 'Lot No.',
      dataIndex: 'lot_no',
      key: 'lot_no',
      searcher: true,
      render: (value, original) => value ? 
      <>
        {value}
        <Tooltip title="รายละเอียดสต๊อกเคมีภัณฑ์">
          <Button
            color='primary'
            variant='link'
            icon={<FilePdfOutlined />}
            onClick={()=> window.open(`${window.origin}${PATH.PURE_CHEMICAL_SUMMARY}/${original.id}`)}
          />
        </Tooltip>
      </> : ""
    },
    {
      title: 'วันที่ผลิต',
      dataIndex: 'produce_date',
      key: 'produce_date',
      render: (value) => value ? toHumanDate(value) : ''
    },
    {
      title: 'วันที่หมดอายุ',
      dataIndex: 'expire_date',
      key: 'expire_date',
      render: (value) => value ? toHumanDate(value) : ''
    },
    {
      title: 'ปริมาณนำเข้า',
      dataIndex: 'amount',
      key: 'amount',
      render : (value) => <center>{currencyFormat(value)}</center>
    },
    {
      title: 'จ่ายออก',
      dataIndex: 'used_amount',
      key: 'used_amount',
      render : (value) => <center>{currencyFormat(value)}</center>
    },
    {
      title: 'คงเหลือ',
      dataIndex: 'remain_amount',
      key: 'remain_amount',
      render : (value) => <center>{currencyFormat(value)}</center>
    },
    {
      title: 'บริษัทผู้จำหน่าย',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      render : (value) => value ?? "-",
      searcher : true
    },
    {
      title: 'สถานะ',
      dataIndex: 'is_closed',
      key: 'is_closed',
      render : (value) =>  <center>{value ? <CloseCircleFilled style={{ color : colorError}}/> : <CheckCircleFilled style={{ color : colorSuccess}}/>}</center>
    },
  ]

  const confirm = (title, value) => {
    Modal.confirm({
      title: `ล้างสต๊อกเคมีภัณฑ์ - ${title}`,
      icon: <ExclamationCircleOutlined style={{ color : colorError}} />,
      content: 'คุณต้องการล้างสต๊อกเคมีภัณฑ์ใช่หรือไม่ ?',
      okText: 'ใช่',
      cancelText: 'ไม่ใช่',
      maskClosable: true,
      okButtonProps: {style: { backgroundColor : colorError}},
      onOk:async() => {
        await handleClearStock(value)
        refreshData();
      },
    });
  };

  const confirmRevert = (title, value) => {
    Modal.confirm({
      title: `ย้อนกลับสต๊อกเคมีภัณฑ์ - ${title}`,
      icon: <ExclamationCircleOutlined style={{ color : colorError}} />,
      content: 'คุณต้องการย้อนกลับสต๊อกเคมีภัณฑ์ใช่หรือไม่ ?',
      okText: 'ใช่',
      cancelText: 'ไม่ใช่',
      maskClosable: true,
      okButtonProps: {style: { backgroundColor : colorError}},
      onOk:async() => {
        await handleRevertStock(value)
        refreshData();
      },
    });
  };

  const handleClearStock = async(id) => {
    setErrorMessages(null)
    try {
      await POST(`${URL_CHEMICAL.PURE_CHEMICAL_TRANSACTION}${id}/close/`)
    } catch (error) {
      setErrorMessages(error.errorMessages)
    }
  }

  const handleRevertStock = async(id) => {
    setErrorMessages(null)
    try {
      await POST(`${URL_CHEMICAL.PURE_CHEMICAL_TRANSACTION}${id}/reactivate/`)
    } catch (error) {
      setErrorMessages(error.errorMessages)
    }
  }

  const onChangeFilter = (value) => {
    setFilterClose(value)
  };

  const refreshData = async() => {
    if(tableRef.current)(
      await tableRef.current.fetch()
    )
  }

  const resetFilter = async() => {
    setTableKey(tableKey => tableKey + 1);
  }

  React.useEffect(() => {
    resetFilter();
  },[filterClose])

  return(
    <div>
      <ErrorMessages message={errorMessages}/>
      สถานะ : 
      <Select
        style={{width : 250, marginLeft : 10}}
        value={filterClose}
        placeholder="สถานะการใช้งาน"
        autoComplete="off"
        options={[
          {
            label : "ทั้งหมด",
            value : ''
          },
          {
            label : "ใช้งาน",
            value : false
          },
          {
            label : "ไม่ใช้งาน-หมด",
            value : true
          },
        ]}
        onChange={onChangeFilter}
      />
      <ColorButton
        type={'primary'}
        override={colorSuccess}
        style={{ float : 'right', marginBottom : 10}}
        icon={<PlusCircleFilled />} 
        onClick={() => setStockTarget('add')}>
          {"เพิ่มสต๊อกเคมีภัณฑ์"}
      </ColorButton>

      <TSTable
        key={tableKey}
        ref={tableRef}
        columns={columns}
        pageSize={50}
        size={'small'}
        url={URL_CHEMICAL.PURE_CHEMICAL_TRANSACTION}
        params={{is_closed: filterClose, ordering: filterClose === true ? 'latest_mix_created' : 'chemical_name'}}
      />

      <AddStockChemicalModal
        open={stockTarget ? true : false}
        onClose={() => setStockTarget(null)}
        onUpdate={() => {
          setStockTarget(null)
          refreshData();
        }}
        target={stockTarget}
      />
    </div>
  )
}